import { ArrowUpRight, BookText, Network, ScrollText, Wand2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { RepoStatsResponse } from '../api/types.js';
import { cn } from '../lib/cn.js';
import { useUiStore } from '../state/ui-store.js';

import { DriftBadge } from './drift-badge.js';

export interface GraphStatsOverlayProps {
  readonly slug: string;
  readonly stats: RepoStatsResponse | undefined;
  readonly loading: boolean;
  readonly error: unknown;
}

export function GraphStatsOverlay({
  slug,
  stats,
  loading,
  error,
}: GraphStatsOverlayProps): JSX.Element {
  if (loading) {
    return <Shell title={slug}>Loading repo stats…</Shell>;
  }
  if (error || !stats) {
    return <Shell title={slug}>Failed to load stats</Shell>;
  }
  const { repo, meta } = stats;
  const s = meta?.stats ?? {};
  return (
    <Shell title={repo.displayName}>
      <div className="flex items-center gap-2">
        <DriftBadge status={repo.lastRefreshStatus} lastIndexedAt={repo.lastIndexedAt} />
        <span className="font-mono text-[10px] text-text-muted">{repo.slug}</span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <Stat label="Files" value={s.files} />
        <Stat label="Symbols" value={s.symbols} />
        <Stat label="Processes" value={s.processes} />
        <Stat label="Communities" value={s.communities} />
      </div>
      <nav className="flex flex-wrap gap-1.5">
        <NavChip to={`/repos/${repo.slug}/explore`} icon={<Network className="h-3 w-3" />}>
          Explore
        </NavChip>
        <NavChip to={`/repos/${repo.slug}/impact`} icon={<Wand2 className="h-3 w-3" />}>
          Impact
        </NavChip>
        <NavChip to={`/repos/${repo.slug}/cypher`} icon={<ScrollText className="h-3 w-3" />}>
          Cypher
        </NavChip>
        <NavChip to={`/repos/${repo.slug}/wiki`} icon={<BookText className="h-3 w-3" />}>
          Wiki
        </NavChip>
      </nav>
      <FocusDepthControl />
    </Shell>
  );
}

function Shell({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="absolute left-3 top-3 z-[4] flex max-w-sm animate-fade-in flex-col gap-2.5 rounded-2xl border border-border-subtle bg-surface/85 px-4 py-3 shadow-panel backdrop-blur-md">
      <div className="flex items-center gap-2">
        <span
          className="grid h-6 w-6 place-items-center rounded-md bg-gradient-to-br from-accent to-fuchsia-500 text-[10px] font-bold text-white shadow-glow-soft"
          aria-hidden
        >
          ◇
        </span>
        <h2 className="truncate text-sm font-semibold text-text-primary">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number | undefined;
}): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-text-muted">
        {label}
      </span>
      <span className="text-base font-semibold text-text-primary">{value ?? '—'}</span>
    </div>
  );
}

function NavChip({
  to,
  icon,
  children,
}: {
  readonly to: string;
  readonly icon: React.ReactNode;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-1 rounded-full border border-border-subtle bg-elevated px-2.5 py-1 text-[11px] text-text-secondary transition-all hover:border-accent/40 hover:bg-accent/10 hover:text-text-primary"
    >
      {icon}
      <span>{children}</span>
      <ArrowUpRight className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

function FocusDepthControl(): JSX.Element {
  const focusDepth = useUiStore((s) => s.focusDepth);
  const setFocusDepth = useUiStore((s) => s.setFocusDepth);
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-text-muted">
        Focus depth
      </span>
      <div className="flex gap-1">
        {([1, 2, 3] as const).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setFocusDepth(d)}
            className={cn(
              'h-5 w-5 rounded font-mono text-[11px] transition-colors',
              focusDepth === d
                ? 'bg-accent text-white'
                : 'border border-border-subtle text-text-muted hover:border-border-default hover:text-text-primary',
            )}
          >
            {d}
          </button>
        ))}
      </div>
    </div>
  );
}
